import React, { useState, useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DataProvider } from "./context/DataContext";
import AuthForm from "./components/AuthForm";
import DeviasKit from "./components/DeviasKit.js";
import LoadingScreen from "./components/LoadingScreen";
import syncService from "./services/syncService";
import Toast from "./components/Toast";

function AppContent() {
  const { user, isAuthenticated, loading } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [activeTruckIndex, setActiveTruckIndex] = useState(-1);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeMsg, setWelcomeMsg] = useState("");
  const prevAuthRef = useRef(false);

  useEffect(() => {
    // This effect runs when the `trucks` array changes.
    // If a truck was added, and we want to make it active:
    if (
      trucks.length > 0 &&
      (activeTruckIndex === -1 || activeTruckIndex >= trucks.length)
    ) {
      // If no truck was active, or previous active index is now invalid (e.g. after deletion, or if it was the placeholder for a new truck)
      // Set active index to the last truck (newly added one)
      setActiveTruckIndex(trucks.length - 1);
    } else if (trucks.length === 0 && activeTruckIndex !== -1) {
      // All trucks removed, reset active index
      setActiveTruckIndex(-1);
    }
  }, [trucks]); // Rerun when trucks array reference changes

  // Show welcome toast once per session after login
  useEffect(() => {
    const prev = prevAuthRef.current;
    if (isAuthenticated && user) {
      const key = `welcomed:${user.id || user.firebase_uid || "anon"}`;
      const already = sessionStorage.getItem(key);
      // Trigger when transitioning to authenticated, or if not shown yet in this session
      if ((prev !== true || !already)) {
        const name = user.name || user.email || "there";
        setWelcomeMsg(`Welcome, ${name}!`);
        setShowWelcome(true);
        sessionStorage.setItem(key, "1");
      }
    }
    prevAuthRef.current = isAuthenticated;
  }, [isAuthenticated, user]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <AuthForm />;
  }

  return (
    <DataProvider>
      <DeviasKit
        trucks={trucks}
        setTrucks={setTrucks}
        activeTruckIndex={activeTruckIndex}
        setActiveTruckIndex={setActiveTruckIndex}
      />
      {showWelcome && (
        <Toast
          title="Signed in"
          message={welcomeMsg}
          duration={3200}
          onClose={() => setShowWelcome(false)}
        />
      )}
    </DataProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
